import React from 'react';
import { 
  Zap, 
  Bot, 
  Users, 
  Workflow, 
  DollarSign, 
  Shield,
  Check,
  Layers,
  Megaphone,
  Inbox,
  Cog,
  PieChart,
  Award,
  BarChart3
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderPill from './AnimatedBorderPill';

interface PillarCard {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ElementType;
  tools: string[];
  backgroundIcon: React.ElementType;
}

const PillarsOfProfitSection: React.FC = () => {
  const { theme } = useTheme();
  
  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#f4f2f1';
  const cardBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const cardBorderColor = theme === 'dark' ? '#333333' : '#E0E0E0';
  const cardShadow = theme === 'dark' 
    ? '0 20px 40px -12px rgba(0,0,0,0.4), 0 8px 20px -8px rgba(0,0,0,0.3)'
    : '0 20px 40px -12px rgba(0,0,0,0.12), 0 8px 20px -8px rgba(0,0,0,0.06)';
  const glowBg = theme === 'dark'
    ? 'radial-gradient(ellipse at center, rgba(255,255,255,0.05) 0%, transparent 70%)'
    : 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 0%, transparent 70%)';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const toolTextColor = theme === 'dark' ? '#CCCCCC' : '#333333';
  const iconColor = theme === 'dark' ? '#888888' : '#666666';
  const badgeBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderGradient = theme === 'dark' 
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';
  const badgeBorderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const lineColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const gridLineColor = theme === 'dark' ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  const pillars: PillarCard[] = [
    {
      title: 'Growth',
      subtitle: 'Attract & Capture',
      description: 'How your story finds its audience and turns attention into leads.',
      icon: Zap,
      backgroundIcon: Megaphone,
      tools: [
        'Social Planner',
        'Email Marketing',
        'SMS & WhatsApp Marketing',
        'Ad Campaign Manager',
        'Surveys & Logic Forms'
      ]
    },
    {
      title: 'Intelligence',
      subtitle: 'The AI Workforce',
      description: 'The "Absolutely Incredible" agents that script your success 24/7.',
      icon: Bot,
      backgroundIcon: BarChart3,
      tools: [
        'Conversation AI / Voice AI',
        'AI Content Engine',
        'AI Funnel & Page Builder',
        'AI Review Responder',
        'Conversation Summary'
      ]
    },
    {
      title: 'Connect',
      subtitle: 'Unified Communications',
      description: 'Every interaction, bound into a single, searchable thread.',
      icon: Users,
      backgroundIcon: Inbox,
      tools: [
        'Unified Conversation Inbox',
        'Missed Call Text-Back',
        'Mobile App',
        'Live Chat Widget',
        'CRM / Contact Manager'
      ]
    },
    {
      title: 'Flow',
      subtitle: 'Automation & Frictionless Journeys',
      description: 'The script that moves your business forward on autopilot.',
      icon: Workflow,
      backgroundIcon: Cog,
      tools: [
        'Workflow Builder',
        'Speed-to-Lead Engine',
        'Smart Lists & Segmentation',
        'Tagging System',
        'Automated Onboarding & Fulfillment'
      ]
    },
    {
      title: 'Profit',
      subtitle: 'Sales, Pipelines & Revenue',
      description: 'Maximize your margins and visualize your financial victory.',
      icon: DollarSign,
      backgroundIcon: PieChart,
      tools: [
        'Visual Sales Pipelines',
        'Calendar & Booking',
        'Payments & Invoicing',
        'E-Sign & Documents',
        'Full Attribution Reporting'
      ]
    },
    {
      title: 'Trust',
      subtitle: 'Reputation, Community & Authority',
      description: 'Turning your expertise into a legacy and your customers into fans.',
      icon: Shield,
      backgroundIcon: Award,
      tools: [
        'Reputation Management',
        'Website & Funnel Builder',
        'Course & Membership Builder',
        'Communities',
        'E-commerce Suite'
      ]
    },
  ];

  const CardComponent = ({ pillar }: { pillar: PillarCard }) => {
    const Icon = pillar.icon;
    const BackgroundIcon = pillar.backgroundIcon;
    
    return (
      <div className="relative h-full">
        {/* Glow/shadow effect behind the card */}
        <div 
          className="absolute -inset-3 rounded-2xl blur-xl opacity-40"
          style={{
            background: glowBg,
          }}
        />
        <div 
          className="relative p-5 flex flex-col h-full min-h-[280px] transition-all duration-300 ease-out hover:shadow-lg hover:-translate-y-1 cursor-pointer overflow-hidden group"
          style={{ 
            backgroundColor: cardBgColor,
            border: `1px solid ${cardBorderColor}`, 
            borderRadius: '12px',
            boxShadow: cardShadow,
          }}
        >
        {/* Icon in box style with grid pattern - matching /compare pages */}
        <div className="relative z-10 mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
            style={iconBoxStyle}
          >
            <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={1.5} />
          </div>
        </div>
        
        {/* Title */}
        <div className="relative z-10 mb-1">
          <h3 
            className="text-lg font-bold"
            style={{ color: headingColor }}
          >
            {pillar.title}
          </h3>
        </div>
        {/* Subtitle */}
        <div className="relative z-10 mb-3">
          <span 
            className="text-[10px] font-medium px-2 py-1 rounded-full"
            style={{ 
              backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              color: textColor
            }}
          >
            {pillar.subtitle}
          </span>
        </div>
        
        <p 
          className="relative z-10 text-xs leading-relaxed mb-4"
          style={{ color: textColor }}
        >
          {pillar.description}
        </p>
        
        <div className="relative z-10 mt-auto">
          <ul className="space-y-1.5">
            {pillar.tools.map((tool, index) => (
              <li key={index} className="flex items-start gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-2.5 h-2.5 text-emerald-600" />
                </div>
                <span className="text-xs" style={{ color: toolTextColor }}>{tool}</span>
              </li>
            ))}
          </ul>
        </div>
        </div>
      </div>
    );
  };

  return (
    <section className="relative py-12 lg:py-16" style={{ backgroundColor: bgColor }}>
      {/* Vertical grid lines */}
      <div 
        className="absolute top-0 bottom-0 left-[5%] md:left-[calc(50%-616px)] w-px pointer-events-none z-0"
        style={{ backgroundColor: lineColor }}
      />
      <div 
        className="absolute top-0 bottom-0 right-[5%] md:right-[calc(50%-616px)] w-px pointer-events-none z-0"
        style={{ backgroundColor: lineColor }}
      />
      
      {/* Bottom horizontal lines (double) */}
      <div 
        className="absolute bottom-[38px] left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
        style={{ 
          background: `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 95%, transparent)`
        }}
      />
      <div 
        className="absolute bottom-0 left-[5%] md:left-[calc(50%-616px)] right-[5%] md:right-[calc(50%-616px)] h-px pointer-events-none z-0"
        style={{ 
          background: `linear-gradient(to right, transparent, ${lineColor} 5%, ${lineColor} 95%, transparent)`
        }}
      />

      <div className="relative z-10 max-w-[1100px] mx-auto px-[7%] sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          {/* Badge - uses stable global CSS classes */}
          <div className="flex justify-center mb-4">
            <AnimatedBorderPill text="The Framework" icon={Layers} />
          </div>
          
          <h2 
            className="text-3xl lg:text-4xl font-bold tracking-tight"
            style={{ color: headingColor }}
          >
            The Pillars of Profit...
          </h2>
        </div>
        
        {/* Bento Grid - 6 cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          {pillars.map((pillar, index) => (
            <div key={index}>
              <CardComponent pillar={pillar} />
            </div>
          ))}
        </div>
      </div>

    </section>
  );
};

export default PillarsOfProfitSection;
